/**
 * MaintenanceModeDialog — Phase 178.6.G Part 4.
 *
 * Premium shadcn replacement for the slot-level `window.prompt` flow that
 * was queued in Phase 178.6.F.  Backed by `POST /api/arenax/slots/{id}/
 * maintenance` (built in 178.6.F).
 *
 *   • Optional reason (chip presets + free text)
 *   • Optional internal note
 *   • Calm prose preview of what changes
 */
import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Wrench, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { track } from '@/lib/analytics';

const REASON_PRESETS = ['Renovation', 'Surface repair', 'Weather', 'Equipment fault', 'Cleaning'];

export default function MaintenanceModeDialog({ slot, open, onClose, onSuccess }) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (slot && open) {
      setReason('');
      setNote('');
      track('dialog_opened', { dialog: 'slot_maintenance', slot_id: slot.id });
    }
  }, [slot, open]);

  if (!slot) return null;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const r = await api.post(`/arenax/slots/${slot.id}/maintenance`, {
        reason: (reason || '').trim() || null,
        note: (note || '').trim() || null,
      });
      track('dialog_submitted', {
        dialog: 'slot_maintenance', slot_id: slot.id,
        idempotent: r.data?.idempotent,
      });
      toast.success(r.data?.idempotent ? 'Slot already under maintenance' : 'Slot in maintenance');
      onSuccess?.(r.data);
      onClose?.();
    } catch (err) {
      track('dialog_failed', { dialog: 'slot_maintenance', slot_id: slot.id, error: err?.response?.status });
      toast.error(err?.response?.data?.detail || 'Could not enter maintenance');
    } finally { setSubmitting(false); }
  };

  const handleCancel = () => {
    track('dialog_cancelled', { dialog: 'slot_maintenance', slot_id: slot?.id });
    onClose?.();
  };

  const startStr = slot.start_at ? new Date(slot.start_at).toLocaleString() : '—';
  const endStr = slot.end_at ? new Date(slot.end_at).toLocaleString() : '—';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleCancel()}>
      <DialogContent className="sm:max-w-md" data-testid="slot-maintenance-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5" /> Slot maintenance
          </DialogTitle>
          <DialogDescription className="leading-relaxed pt-1">
            Mark this slot as under maintenance. It becomes unbookable
            immediately and surfaces with a wrench glyph in the calendar.
            Idempotent — re-applying does nothing.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs uppercase tracking-wider">Slot window</Label>
            <div className="text-sm font-mono mt-1" style={{ color: 'hsl(var(--muted-foreground))' }}>
              {startStr} → {endStr}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider">Reason (optional)</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="slot-maintenance-presets">
              {REASON_PRESETS.map((p) => (
                <Button key={p} size="sm" type="button"
                        variant={reason === p ? 'default' : 'outline'}
                        onClick={() => setReason(p)}
                        data-testid={`slot-maintenance-preset-${p.toLowerCase().replace(/\s+/g, '-')}`}>
                  {p}
                </Button>
              ))}
            </div>
            <Input className="mt-2" placeholder="Or type your own…"
                   value={reason} onChange={(e) => setReason(e.target.value)}
                   maxLength={80}
                   data-testid="slot-maintenance-reason-input" />
          </div>

          <div>
            <Label htmlFor="slot-maintenance-note" className="text-xs uppercase tracking-wider">
              Internal note (optional)
            </Label>
            <Input id="slot-maintenance-note" placeholder="Stays in the audit ledger…"
                   value={note} onChange={(e) => setNote(e.target.value)}
                   maxLength={200} className="mt-1"
                   data-testid="slot-maintenance-note-input" />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleCancel} disabled={submitting}
                  data-testid="slot-maintenance-cancel">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}
                  data-testid="slot-maintenance-confirm">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wrench className="h-4 w-4" />}
            Enter maintenance
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
